/**
 * Audit Zustand Store
 *
 * Manages all state for the Audit Log section including:
 * - Audit log list with pagination
 * - Filters for actions, users, entities, date range
 * - Audit statistics
 * - Export functionality
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  SuperAdminAuditLog,
  AuditLogFilters,
  AuditStats,
  PaginationMeta,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface AuditError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface AuditState {
  // Audit Logs
  auditLogs: SuperAdminAuditLog[];
  pagination: PaginationMeta | null;
  filters: AuditLogFilters;
  isLoadingLogs: boolean;
  logsError: AuditError | null;

  // Selected Log Detail
  selectedLogId: string | null;
  selectedLog: SuperAdminAuditLog | null;
  isLoadingLog: boolean;

  // Stats
  auditStats: AuditStats | null;
  isLoadingStats: boolean;
  statsError: AuditError | null;

  // Export
  isExporting: boolean;
  exportError: AuditError | null;

  // Actions - Logs
  loadAuditLogs: (page?: number) => Promise<void>;
  loadAuditLog: (id: string) => Promise<void>;
  setFilter: <K extends keyof AuditLogFilters>(
    key: K,
    value: AuditLogFilters[K]
  ) => void;
  clearFilters: () => void;
  setSelectedLogId: (id: string | null) => void;

  // Actions - Stats
  loadStats: () => Promise<void>;

  // Actions - Export
  exportLogs: (format: 'csv' | 'json') => Promise<string | null>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Audit Logs
  auditLogs: [] as SuperAdminAuditLog[],
  pagination: null as PaginationMeta | null,
  filters: {} as AuditLogFilters,
  isLoadingLogs: false,
  logsError: null as AuditError | null,

  // Selected Log Detail
  selectedLogId: null as string | null,
  selectedLog: null as SuperAdminAuditLog | null,
  isLoadingLog: false,

  // Stats
  auditStats: null as AuditStats | null,
  isLoadingStats: false,
  statsError: null as AuditError | null,

  // Export
  isExporting: false,
  exportError: null as AuditError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useAuditStore = create<AuditState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // Audit Log Actions
  // ============================================

  loadAuditLogs: async (page = 1) => {
    set({ isLoadingLogs: true, logsError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '25');

      // Apply filters
      if (filters.search) params.set('search', filters.search);
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.action) params.set('action', filters.action);
      if (filters.entityType) params.set('entityType', filters.entityType);
      if (filters.entityId) params.set('entityId', filters.entityId);
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.startDate) params.set('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.set('endDate', filters.endDate.toISOString());

      const response = await api.get<{
        data: SuperAdminAuditLog[];
        pagination: PaginationMeta;
      }>(`${API_ENDPOINTS.SUPER_ADMIN.AUDIT.LIST}?${params.toString()}`);

      set({
        auditLogs: response.data,
        pagination: response.pagination,
        isLoadingLogs: false,
      });
    } catch (error) {
      set({
        isLoadingLogs: false,
        logsError: {
          code: 'LOAD_LOGS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load audit logs',
        },
      });
    }
  },

  loadAuditLog: async (id: string) => {
    set({ isLoadingLog: true, selectedLogId: id });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: SuperAdminAuditLog;
      }>(API_ENDPOINTS.SUPER_ADMIN.AUDIT.GET(id));

      set({
        selectedLog: response.data,
        isLoadingLog: false,
      });
    } catch (error) {
      set({
        isLoadingLog: false,
        logsError: {
          code: 'LOAD_LOG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load audit log',
        },
      });
    }
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadAuditLogs(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadAuditLogs(1);
  },

  setSelectedLogId: (id: string | null) => {
    set({ selectedLogId: id });
    if (id) {
      get().loadAuditLog(id);
    } else {
      set({ selectedLog: null });
    }
  },

  // ============================================
  // Stats Actions
  // ============================================

  loadStats: async () => {
    set({ isLoadingStats: true, statsError: null });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: AuditStats;
      }>(API_ENDPOINTS.SUPER_ADMIN.AUDIT.STATS);

      set({
        auditStats: response.data,
        isLoadingStats: false,
      });
    } catch (error) {
      set({
        isLoadingStats: false,
        statsError: {
          code: 'LOAD_STATS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load audit stats',
        },
      });
    }
  },

  // ============================================
  // Export Actions
  // ============================================

  exportLogs: async (format: 'csv' | 'json'): Promise<string | null> => {
    set({ isExporting: true, exportError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();
      params.set('format', format);

      // Apply current filters to export
      if (filters.search) params.set('search', filters.search);
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.action) params.set('action', filters.action);
      if (filters.entityType) params.set('entityType', filters.entityType);
      if (filters.entityId) params.set('entityId', filters.entityId);
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.startDate) params.set('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.set('endDate', filters.endDate.toISOString());

      const response = await api.get<{
        success: boolean;
        data: { downloadUrl: string };
      }>(`${API_ENDPOINTS.SUPER_ADMIN.AUDIT.EXPORT}?${params.toString()}`);

      set({ isExporting: false });
      return response.data.downloadUrl;
    } catch (error) {
      set({
        isExporting: false,
        exportError: {
          code: 'EXPORT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to export audit logs',
        },
      });
      return null;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({
      logsError: null,
      statsError: null,
      exportError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get active filters count */
export const useActiveFiltersCount = () => {
  return useAuditStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.userId) count++;
    if (state.filters.action) count++;
    if (state.filters.entityType) count++;
    if (state.filters.entityId) count++;
    if (state.filters.dateRange && state.filters.dateRange !== 'all') count++;
    if (state.filters.startDate) count++;
    if (state.filters.endDate) count++;
    return count;
  });
};

/** Get logs by action type */
export const useLogsByAction = (action: string) => {
  return useAuditStore((state) =>
    state.auditLogs.filter((log) => log.action === action)
  );
};

/** Get recent N logs */
export const useRecentLogs = (limit = 10) => {
  return useAuditStore((state) => state.auditLogs.slice(0, limit));
};

/** Check if any audit data is loading */
export const useAuditLoading = () => {
  const isLoadingLogs = useAuditStore((state) => state.isLoadingLogs);
  const isLoadingStats = useAuditStore((state) => state.isLoadingStats);
  const isExporting = useAuditStore((state) => state.isExporting);
  return isLoadingLogs || isLoadingStats || isExporting;
};

/** Get total logs count from stats */
export const useTotalLogsCount = () => {
  return useAuditStore((state) => state.auditStats?.totalLogs ?? 0);
};

/** Get logs today count from stats */
export const useLogsTodayCount = () => {
  return useAuditStore((state) => state.auditStats?.logsToday ?? 0);
};
